import React, { useState, useEffect } from "react";
import moment from "moment/moment";

import { ApiCallResult, useAPI } from "services/Api";
import ExternalReportsRow from "pages/ExternalReportsRow";
import Input from "input/Input";
import Page from "pages/Page";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";
import { ModularHeaderConfig } from "./ModularHeader";

const ExternalReports = ({ componentName = "[ExternalReports]" }) => {
    const [filter, setFilter] = useState(null);
    const [filteredPatients, setFilteredPatients] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [outcomeReports, setOutcomeReports] = useState(null);
    const [patients, setPatients] = useState(null);
    const [shouldReceiveApprovedReports, setShouldReceiveApprovedReports] = useState(false);
    const [user, setUser] = useState(null);

    const API = useAPI();
    const notificationManager = useNotificationManager();

    // ---==== KarunaCLOUD ====--- //

    const api_GetUser = async (): Promise<ApiCallResult> => {
        return await API.api_Get(
            componentName,
            `/user/`,
            notificationManager,
            (response) => {
                let user = response.data;
                setUser(user);
                setShouldReceiveApprovedReports(
                    user.should_receive_approved_outcome_reports
                );
            }
        );
    };

    const api_UpdateUser = async (receiveApprovedReports): Promise<ApiCallResult> => {
        return await API.api_Patch(
            componentName,
            `/user/`,
            JSON.stringify({
                first_name: user.first_name,
                last_name: user.last_name,
                should_receive_approved_outcome_reports: receiveApprovedReports
            }),
            notificationManager,
            (response) => {
                let user = response.data;
                setShouldReceiveApprovedReports(
                    user.should_receive_approved_outcome_reports
                );
                notificationManager.addSuccess("Updated Email Report Preference", { duration: 10 });
            },
            (error) => {
                console.error(error);
                notificationManager.addError(error)
            }
        );
    };

    const api_GetPatients = async (): Promise<ApiCallResult> => {
        return await API.api_Get(
            componentName,
            `/patients/`,
            notificationManager,
            (response) => {
                setPatients(response.data.results);
            });
    };

    const api_GetOutcomeReports = async (): Promise<ApiCallResult> => {
        return await API.api_Get(
            componentName,
            "/outcome_reports/",
            notificationManager,
            (response) => {
                let reports = response.data.results;
                if (reports) {
                    reports = reports.sort(
                        (a, b) => {
                            return moment(a.prepared_date).isBefore(moment(b.prepared_date)) ? 1 : -1;
                        }
                    );
                }
                setOutcomeReports(reports ? reports : []);
            }
        );
    };

    // ---==== Utilities ====--- //

    const dispatchUpdateUserEmailPreferences = (event) => {
        event.preventDefault();
        let receiveApprovedReports = event.target.checked;
        void api_UpdateUser(receiveApprovedReports);
    };

    const isAllRequiredDataAvailable = () => {
        return user && patients && outcomeReports;
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            const loadData = async () => {
                try {
                    let result: ApiCallResult = await api_GetUser();

                    if (result.wasSuccessful) {
                        result = await api_GetPatients();
                    }

                    if (result.wasSuccessful) {
                        result = await api_GetOutcomeReports();
                    }

                    if (!result.wasSuccessful) {
                        setIsLoading(false);
                    }
                }
                catch (error) {
                    console.error(`${componentName} Unexpected error loading data:`, error);
                    notificationManager.addError(["An unexpected error occurred while loading data", error]);
                    setPatients([]);
                    setOutcomeReports([]);
                }
            };

            void loadData();
        }, []
    );

    useEffect(
        () => {
            if (patients) {
                const normalizedFilter = filter ? filter.trim().toLowerCase() : "";
                const filtered = patients.filter((patient) => {
                    let patientName = `${patient.first_name} ${patient.last_name}`;
                    return filter
                        ? patientName.toLowerCase().includes(normalizedFilter)
                        : true;
                });
                setFilteredPatients(filtered);
            }
        }, [patients, filter]
    );

    useEffect(
        () => {
            if (isAllRequiredDataAvailable()) {
                if (isLoading) {
                    setIsLoading(false);
                }
            }
        }, [isLoading, user, patients, outcomeReports]
    );

    // ---==== Render ====--- //

    const getModularHeaderConfig = (): ModularHeaderConfig => {
        return {
            trail: breadCrumbLinks()
        };
    };

    const breadCrumbLinks = () => {
        return [
            {
                label: "Reports",
                url: "/reportsExternal"
            }
        ];
    };

    const renderHeader = () => {
        return (
            <React.Fragment>
                <div className="w-full h-[10%] inline-flex justify-start items-center gap-4 z-40 relative pt-5 pb-[19px]">
                    <div className="w-[50%] h-full flex items-center justify-start gap-5 ml-4">
                        <div className="text-gray-900 text-lg font-medium leading-7">
                            Patient Outcome Reports
                        </div>
                        <div className="w-[100%] h-full flex grow shrink items-center gap-2">
                            <input
                                className="w-3.5 h-3.5 p-0.5 bg-gray-100 rounded border focus:ring-0 cursor-pointer"
                                type="checkbox"
                                checked={shouldReceiveApprovedReports}
                                onChange={
                                    (event) => {
                                        dispatchUpdateUserEmailPreferences(event);
                                    }
                                }
                            />
                            <div className="w-[100%] grow shrink whitespace-nowrap text-gray-700 text-xs leading-tight">
                                Receive Emails for Future Reports
                            </div>
                        </div>
                    </div>
                    <div className="grow shrink basis-0 h-5 px-[25px] justify-end items-center gap-2 flex"/>
                    <div className="w-[25%] flex-col justify-start items-start inline-flex">
                        <div className="self-stretch h-full flex items-start justify-start gap-0">
                            <div className="self-stretch pl-3.5 pr-1 py-1.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex">
                                <div className="grow shrink basis-0 h-full justify-start items-center gap-2 flex">
                                    <img
                                        className="w-6 h-6 relative"
                                        src="/icons/search.svg"
                                        alt="search"
                                    />
                                    <Input
                                        className="w-full h-6 grow shrink overflow-hidden basis-0 font-normal leading-normal text-gray-500 text-base outline-none border-0 focus:outline-none active:border-0 focus:border-0 focus:ring-0"
                                        name="Search Names"
                                        overrideText={"Search Names"}
                                        required
                                        type="text"
                                        value={filter ? filter : ""}
                                        // Callbacks
                                        onChange={setFilter}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const renderNoReportsFound = () => {
        return (
            <React.Fragment>
                <div className="w-full h-full justify-center items-center inline-flex flex-col">
                    <p className="px-6 text-gray-900 text-lg font-medium ">
                        No Reports Found
                    </p>
                </div>
            </React.Fragment>
        );
    };

    const renderPatientReportRows = () => {
        let patientReportRows = [];

        filteredPatients.forEach(
            (patient) => {
                let sortedReports = outcomeReports
                    .filter((report) => report.patient_id === patient.id)
                    .sort(
                        (a, b) => {
                            return moment(a.prepared_date).isBefore(moment(b.prepared_date)) ? 1 : -1;
                        }
                    );

                patientReportRows.push(
                    <ExternalReportsRow
                        notificationManager={notificationManager}
                        parentComponentName={componentName}
                        patient={patient}
                        patientOutcomeReports={sortedReports}
                    />
                );
            });

        return patientReportRows;
    };

    const renderTable = () => {
        return (
            <div className="grid grid-col w-full whitespace-nowrap overflow-y-auto">
                <table className="table-auto w-full border-collapse border-x-0">
                    <thead className="whitespace-nowrap	w-full">
                        <tr className="whitespace-nowrap sticky shadow-[inset_0_1px_0_#d3d3d3,inset_0_-1px_0_#d3d3d3] h-[58px] py-3 bg-gray-50 text-gray-500 border-gray-200 border-x-0 border-y-2">
                            <th scope="col" className="w-auto px-6 text-left   text-xs font-medium text-gray-500">Patient</th>
                            <th scope="col" className="w-auto pr-4 text-center text-xs font-medium text-gray-500">Reports</th>
                            <th scope="col" className="w-auto px-8 text-center text-xs font-medium text-gray-500"></th>
                            <th scope="col" className="w-auto px-4 text-left   text-xs font-medium text-gray-500">Physician</th>
                            <th scope="col" className="w-auto px-4 text-left   text-xs font-medium text-gray-500">TPA</th>
                            <th scope="col" className="w-auto px-4 text-left   text-xs font-medium text-gray-500">Insurer</th>
                        </tr>
                    </thead>
                    <tbody className="whitespace-nowrap">
                        {renderPatientReportRows()}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Page
            componentName={componentName}
            headerConfig={getModularHeaderConfig()}
            internal={true}
        >
            <div className="w-full h-full flex items-start my-8">
                {
                    isLoading
                        ? (
                            renderLoader()
                        )
                        : (
                            <React.Fragment>
                                <div id="table-margin-left-fixed" className={"w-20 flex-shrink-0"}/>
                                <div id="table-container-stretchy" className="w-full flex flex-grow items-start justify-center whitespace-nowrap overflow-hidden">
                                    <div id="table-container" className={"w-fit flex flex-col overflow-hidden bg-white border-2 border-gray-200 rounded-lg shadow-lg min-w-0"}>
                                        {renderHeader()}
                                        {filteredPatients?.length > 0 ? renderTable() : renderNoReportsFound()}
                                    </div>
                                </div>
                                <div id="table-margin-right-shrink" className={"w-20 flex-shrink-0"}/>
                            </React.Fragment>
                        )
                }
            </div>
        </Page>
    );
};

export default ExternalReports;
